import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Box,
  Alert,
  Tooltip,
  Divider,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { API_ENDPOINT } from "../../utils/constants";
import { getDbHeader } from "../../utils/util";
import { toast } from "react-toastify";
import BackBtn from "../../widgets/BackBtn";

function GtagConfigForm() {
  const [trackingId, setTrackingId] = useState(""); // Google Analytics tracking ID
  const [gtmId, setGtmId] = useState(""); // New GTM ID state
  const [anonymizeIp, setAnonymizeIp] = useState(true);
  const [sendPageView, setSendPageView] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [error, setError] = useState("");

  // Load existing config on mount
  useEffect(() => {
    fetch(`${API_ENDPOINT}event/get-gtag-config`, {
      ...getDbHeader(),
      method: "GET",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.gtag) {
          setTrackingId(data.gtag.trackingId || "");
          setGtmId(data.gtag.gtmId || ""); // Load GTM ID if present
          setAnonymizeIp(data.gtag.additionalConfig?.anonymize_ip ?? true);
          setSendPageView(data.gtag.additionalConfig?.send_page_view ?? true);
        }
      })
      .catch((err) => setError("Failed to load config"));
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    setIsSaved(false);

    const additionalConfig = {
      anonymize_ip: anonymizeIp,
      send_page_view: sendPageView,
    };

    // Include gtmId in the payload
    fetch(`${API_ENDPOINT}event/save-gtag-config`, {
      method: "POST",
      ...getDbHeader(),
      body: JSON.stringify({ trackingId, gtmId, additionalConfig }),
    })
      .then((res) => res.json())
      .then((data) => {
        setIsSaved(true);
        toast.success(data.message);
      })
      .catch((err) => setError("Network error"));
  };

  return (
    <Container sx={{ py: 4 }}>
      <BackBtn />
      <Paper elevation={3} sx={{ p: 3, maxWidth: 700, margin: "auto" }}>
        <Typography variant="h5" gutterBottom>
          Configure Tracking Settings
        </Typography>
        <Typography variant="body1" color="textSecondary" paragraph>
          Set up Google Analytics (gtag.js) and Google Tag Manager (GTM) for
          your app. These settings enable dynamic tracking in your React app
          without hardcoding scripts in <code>index.html</code>.
        </Typography>

        {isSaved && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Settings saved successfully!
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          {/* Google Analytics Tracking ID */}
          <TextField
            label="Google Analytics Tracking ID"
            value={trackingId}
            onChange={(e) => setTrackingId(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="e.g., G-XXXXXXXXXX"
            InputProps={{
              endAdornment: (
                <Tooltip
                  title="The unique ID (e.g., G-XXXXXXXXXX) from your Google Analytics property. It tells Analytics where to send your app's tracking data."
                  arrow
                >
                  <InfoIcon color="action" sx={{ cursor: "pointer" }} />
                </Tooltip>
              ),
            }}
            helperText="Enter your Google Analytics tracking ID (optional if using GTM)."
          />

          {/* Google Tag Manager ID */}
          <TextField
            label="Google Tag Manager ID"
            value={gtmId}
            onChange={(e) => setGtmId(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="e.g., GTM-XXXXXXX"
            InputProps={{
              endAdornment: (
                <Tooltip
                  title="The unique ID (e.g., GTM-XXXXXXX) from your Google Tag Manager container. Use it to manage tags for Facebook, LinkedIn, and other analytics platforms."
                  arrow
                >
                  <InfoIcon color="action" sx={{ cursor: "pointer" }} />
                </Tooltip>
              ),
            }}
            helperText="Enter your GTM ID to enable multi-platform tracking (optional)."
          />

          <Divider sx={{ my: 2 }} />

          {/* Anonymize IP */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={anonymizeIp}
                  onChange={(e) => setAnonymizeIp(e.target.checked)}
                />
              }
              label="Anonymize IP Addresses"
            />
            <Tooltip
              title="When enabled, the last part of a user's IP address is hidden (e.g., 192.168.1.123 becomes 192.168.1.0). This protects user privacy but may reduce location accuracy."
              arrow
            >
              <InfoIcon color="action" sx={{ ml: 1, cursor: "pointer" }} />
            </Tooltip>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ ml: 4 }}>
            {anonymizeIp
              ? "IP addresses will be anonymized for better privacy."
              : "Full IP addresses will be tracked, providing more precise location data."}
          </Typography>

          {/* Send Page View */}
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={sendPageView}
                  onChange={(e) => setSendPageView(e.target.checked)}
                />
              }
              label="Send Page Views Automatically (gtag)"
            />
            <Tooltip
              title="When enabled, Google Analytics (gtag) logs a page view on script load. Turn off for manual tracking in React (e.g., via route changes). GTM handles its own triggers separately."
              arrow
            >
              <InfoIcon color="action" sx={{ ml: 1, cursor: "pointer" }} />
            </Tooltip>
          </Box>
          <Typography variant="body2" color="textSecondary" sx={{ ml: 4 }}>
            {sendPageView
              ? "The initial page load will be tracked automatically by gtag."
              : "Page views must be tracked manually with gtag (e.g., via route changes)."}
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* How This Helps Tracking */}
          <Typography variant="subtitle2" gutterBottom>
            How These Settings Help Tracking
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Use the <strong>Google Analytics Tracking ID</strong> for direct
            tracking with <code>gtag.js</code>. The{" "}
            <strong>Google Tag Manager ID</strong> lets you manage multiple
            analytics tools (e.g., Facebook Pixel and LinkedIn Insight) through
            one container. For example, GTM can send page views to{" "}
            <strong>Facebook</strong> to track ad performance or to{" "}
            <strong>LinkedIn</strong> for campaign insights, all configured in
            the GTM dashboard without changing your app’s code. With{" "}
            <strong>Send Page Views</strong> off, you control gtag tracking
            dynamically in React, while GTM handles its own triggers.
          </Typography>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
          >
            Save Configuration
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default GtagConfigForm;
